import React, { useMemo, useEffect } from "react";

import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBan } from "@fortawesome/pro-solid-svg-icons";
import { faTimes } from "@fortawesome/pro-light-svg-icons";

import Loading from "components/Loading";
import { useRelatedRoute, useRoutes } from "utils/routes";
import { useUserPreferences } from "utils/hooks";
import { mru } from "utils/array";

import {
  type DatabaseGroupSelectQuery,
  useDatabaseGroupSelectQuery,
} from "./gql/Query.generated";

type DatabaseGroupType = DatabaseGroupSelectQuery["getDatabaseGroups"][number];

import styles from "../style.module.scss";
import { useCurrentServerGroup } from "components/WithCurrentOrganization";
import { CaretIcon } from "components/Icons";
import MenuList from "../MenuList";
import { ItemRendererProps } from "../types";

function DatabaseGroupSelect() {
  const currentServerGroup = useCurrentServerGroup();
  const currentServerGroupId = currentServerGroup?.id ?? "";
  const { search } = useLocation();
  const currentDatname = new URLSearchParams(search).get("datname");

  const [dbgSelect, setDbgSelect] = useUserPreferences("databaseGroupSelect");

  const recentDatnames = useMemo(() => {
    return (dbgSelect.recentDatnames || {})[currentServerGroupId] || [];
  }, [dbgSelect.recentDatnames, currentServerGroupId]);

  const { relatedServerGroupRoute } = useRelatedRoute();

  useEffect(() => {
    if (currentDatname && recentDatnames[0] != currentDatname) {
      setDbgSelect({
        ...dbgSelect,
        recentDatnames: {
          ...dbgSelect.recentDatnames,
          [currentServerGroupId]: mru(currentDatname, recentDatnames, 5),
        },
      });
    }
  }, [
    setDbgSelect,
    dbgSelect,
    currentServerGroupId,
    recentDatnames,
    currentDatname,
  ]);

  const { data, loading, error } = useDatabaseGroupSelectQuery({
    variables: {
      serverGroupId: currentServerGroupId,
    },
  });
  if (loading || error) {
    return <Loading className={styles.loading} small error={!!error} />;
  }

  const groups = data?.getDatabaseGroups ?? [];
  const visibleGroups = groups.filter((g) => !g.hidden);

  const sortedGroups = visibleGroups.slice().sort((a, b) => {
    if (
      recentDatnames.includes(a.datname) &&
      recentDatnames.includes(b.datname)
    ) {
      return (
        recentDatnames.indexOf(a.datname) - recentDatnames.indexOf(b.datname)
      );
    }
    if (recentDatnames.includes(a.datname)) {
      return -1;
    }
    if (recentDatnames.includes(b.datname)) {
      return 1;
    }
    return a.datname.localeCompare(b.datname);
  });

  const currentGroup = groups.find((g) => g.datname === currentDatname);
  const canOpen =
    visibleGroups.length > 1 || (visibleGroups.length === 1 && !currentGroup);

  const trigger = ({
    open,
    toggleOpen,
  }: {
    open: boolean;
    toggleOpen: () => void;
  }) => (
    <button onClick={toggleOpen}>
      <small>Database</small>
      <div className={styles.selectMenuCurrent}>
        {currentGroup ? (
          currentGroup.datname
        ) : (
          <span>No database selected</span>
        )}
        {currentGroup && (
          <Link
            to={relatedServerGroupRoute(currentServerGroupId)}
            className={styles.closeLink}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Link>
        )}
        <CaretIcon direction={open ? "up" : "down"} headerNav />
      </div>
    </button>
  );

  return (
    <div className={styles.selectMenu} data-select-menu="databaseGroup">
      {canOpen ? (
        <MenuList
          trigger={trigger}
          prompt="Search databases..."
          noMatch="no databases match"
          sections={[{ entries: sortedGroups }]}
          filterKeys={["datname"]}
          renderer={DatabaseGroupItem}
        />
      ) : (
        <div>
          <div className={styles.noOptions}>
            <small>Database</small>
            <div className={styles.selectMenuCurrent}>
              {currentGroup ? (
                currentGroup.datname
              ) : (
                <FontAwesomeIcon
                  icon={faBan}
                  className="text-[#ccc] mt-[1px]"
                />
              )}
              {currentGroup && (
                <Link
                  to={relatedServerGroupRoute(currentServerGroupId)}
                  className={styles.closeLink}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DatabaseGroupItem({
  item: group,
  onSelect,
}: ItemRendererProps<DatabaseGroupType & { datname: string }>) {
  const { serverGroupQueries, database: databaseRoot } = useRoutes();
  const { relatedDatabaseRoute } = useRelatedRoute();
  const currentServerGroup = useCurrentServerGroup();
  const serverGroupId = currentServerGroup?.id ?? "";

  // For pages with a database-scoped equivalent (e.g. workbooks), drill into
  // the first matching database. Otherwise, keep the datname-filtered server
  // group queries behavior.
  const firstDatabaseId = group.databaseIds[0];
  const relatedRoute = firstDatabaseId
    ? relatedDatabaseRoute(firstDatabaseId)
    : null;
  const to =
    firstDatabaseId &&
    relatedRoute &&
    relatedRoute !== databaseRoot(firstDatabaseId)
      ? relatedRoute
      : serverGroupQueries(serverGroupId, group.datname);

  return (
    <Link className={styles.selectMenuListItem} to={to} onClick={onSelect}>
      {group.datname}
    </Link>
  );
}

export default DatabaseGroupSelect;
